import { useMemo } from "react";

import { API_ENDPOINTS } from "../../api/constants";
import useFetch from "../../api/useFetch";

/*
 * Using a company, provide a useful object of booleans that indicate the level of access
 * the user has to the company's data.
 *
 * `hasAnyAccess` is true when the user has any per-user `can_*` permission OR when the
 * company itself exposes data via its `show_order_flow_data` / `show_reported_marks_data`
 * flags. The per-company flags act as a baseline gate so that publicly-available data
 * (e.g. order flow on companies marked show_order_flow_data=true) renders the profile
 * even when the user has no explicit access records.
 */
const useCompanyAccess = company => {
  const companyId = company?.zb_id;
  const hasProDataAccessQueryInfo = useFetch(
    API_ENDPOINTS.HAS_PRO_DATA_ACCESS(companyId),
    {},
    {
      retryOnMount: false,
      enabled: Boolean(companyId),
    }
  );

  const showOrderFlowData = Boolean(company?.show_order_flow_data);
  const showReportedMarksData = Boolean(company?.show_reported_marks_data);

  return useMemo(() => {
    const companyHasVisibleData = showOrderFlowData || showReportedMarksData;

    if (
      hasProDataAccessQueryInfo.isLoading ||
      hasProDataAccessQueryInfo.isError ||
      !hasProDataAccessQueryInfo.cleanedData
    ) {
      return {
        canDownloadPDFReport: false,
        canDownloadZXData: false,
        canViewBidAskPriceGraph: false,
        canViewBidAskRatioGraph: false,
        canViewPriceAndVolumeGraph: false,
        canViewRelativeActivityGraph: false,
        canViewReportedMarksGraph: false,
        canViewSacraDocuments: false,
        canViewClosedTransactions: false,
        isLoading: hasProDataAccessQueryInfo.isLoading,
        isError: hasProDataAccessQueryInfo.isError,
        // Honor the per-company baseline gate even when the access query is
        // unavailable — otherwise public data (show_order_flow_data=true) is
        // hidden during loading/error/empty states.
        hasAnyAccess: companyHasVisibleData,
        timeFrame: null,
      };
    }
    const hasAnyCanFlag = Object.keys(hasProDataAccessQueryInfo.cleanedData)
      .filter(key => key.startsWith("can_"))
      .some(key => hasProDataAccessQueryInfo.cleanedData[key]);
    return {
      canDownloadPDFReport: hasProDataAccessQueryInfo.cleanedData.can_download_pdf_report,
      canDownloadZXData: hasProDataAccessQueryInfo.cleanedData.can_download_zx_data,
      canViewBidAskPriceGraph: hasProDataAccessQueryInfo.cleanedData.can_view_bid_ask_price_graph,
      canViewBidAskRatioGraph: hasProDataAccessQueryInfo.cleanedData.can_view_bid_ask_ratio_graph,
      canViewPriceAndVolumeGraph:
        hasProDataAccessQueryInfo.cleanedData.can_view_price_and_volume_graph,
      canViewRelativeActivityGraph:
        hasProDataAccessQueryInfo.cleanedData.can_view_relative_activity_graph,
      canViewReportedMarksGraph:
        hasProDataAccessQueryInfo.cleanedData.can_view_reported_marks_graph,
      canViewSacraDocuments: hasProDataAccessQueryInfo.cleanedData.can_view_sacra_documents,
      canViewClosedTransactions: hasProDataAccessQueryInfo.cleanedData.can_view_closed_transactions,
      isLoading: hasProDataAccessQueryInfo.isLoading,
      isError: hasProDataAccessQueryInfo.isError,
      hasAnyAccess: hasAnyCanFlag || companyHasVisibleData,
      timeFrame: hasProDataAccessQueryInfo.cleanedData.time_frame,
    };
  }, [hasProDataAccessQueryInfo, showOrderFlowData, showReportedMarksData]);
};

export default useCompanyAccess;
